import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookOpen, FilePdf, VideoCamera, Link as LinkIcon, FolderOpen, DownloadSimple, X, Play } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Md } from '@/components/Md';

const API = process.env.REACT_APP_BACKEND_URL;
const TYPE_ICONS = { pdf: FilePdf, video: VideoCamera, link: LinkIcon };
const EXT_COLORS = { pdf: 'bg-red-50 text-red-600', xlsx: 'bg-emerald-50 text-emerald-600', csv: 'bg-blue-50 text-blue-600', jpg: 'bg-amber-50 text-amber-600', jpeg: 'bg-amber-50 text-amber-600', png: 'bg-amber-50 text-amber-600', mp4: 'bg-purple-50 text-purple-600' };

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // Vimeo
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  // Loom
  m = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (m) return `https://www.loom.com/embed/${m[1]}`;
  return null;
}

function VideoPlayer({ url }) {
  const embedUrl = getEmbedUrl(url);
  if (!embedUrl) return <a href={url} target="_blank" rel="noreferrer" className="text-sm text-brand-green hover:underline block py-4 text-center">Abrir vídeo numa nova janela</a>;
  return (
    <div className="relative w-full pt-[56.25%] rounded-xl overflow-hidden bg-black">
      <iframe src={embedUrl} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Vídeo" />
    </div>
  );
}

export default function ConteudosPage() {
  const [contents, setContents] = useState([]);
  const [driveFiles, setDriveFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('');
  const [activeSection, setActiveSection] = useState('conteudos'); // conteudos, ficheiros
  const [loading, setLoading] = useState(true);
  const [viewContent, setViewContent] = useState(null);

  const fetchContents = useCallback(async () => {
    try {
      const url = activeCat ? `${API}/api/contents?category=${encodeURIComponent(activeCat)}` : `${API}/api/contents`;
      const { data } = await axios.get(url);
      setContents(data);
    } catch {} finally { setLoading(false); }
  }, [activeCat]);

  const fetchDrive = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/my-drive`); setDriveFiles(data); } catch {}
  }, []);

  const fetchCategories = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/content-categories`); setCategories(data.categories || []); } catch {}
  }, []);

  useEffect(() => { fetchContents(); fetchDrive(); }, [fetchContents, fetchDrive]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openContent = (c) => {
    if (c.url) window.open(c.url, '_blank');
    else if (c.file_path) downloadFile(c.file_path, c.original_filename || c.title || 'ficheiro');
  };

  const downloadFile = async (path, name) => {
    try {
      const resp = await axios.get(`${API}/api/files/${path}`, { responseType: 'blob' });
      // Ensure filename has correct extension
      const ext = path.split('.').pop()?.toLowerCase();
      let filename = name;
      if (ext && !filename.toLowerCase().endsWith(`.${ext}`)) {
        filename = `${filename}.${ext}`;
      }
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const totalDrive = driveFiles.length;

  return (
    <div className="fade-in" data-testid="conteudos-page">
      <div className="mb-6">
        <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="conteudos-title">Os meus conteúdos</h2>
        <p className="text-sm text-brand-muted mt-1">Materiais de apoio e ficheiros pessoais</p>
      </div>

      {/* Section toggle */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setActiveSection('conteudos')} className={`px-4 py-2 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${activeSection === 'conteudos' ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid="section-conteudos">
          <BookOpen size={14} /> Conteúdos
        </button>
        <button onClick={() => setActiveSection('ficheiros')} className={`px-4 py-2 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${activeSection === 'ficheiros' ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid="section-ficheiros">
          <FolderOpen size={14} /> Os meus ficheiros {totalDrive > 0 && `(${totalDrive})`}
        </button>
      </div>

      {/* Contents section */}
      {activeSection === 'conteudos' && (
        <>
          {/* Category filters */}
          <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
            <button onClick={() => setActiveCat('')} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${!activeCat ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`}>Todos</button>
            {categories.map(c => (
              <button key={c} onClick={() => setActiveCat(activeCat === c ? '' : c)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${activeCat === c ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`cat-${c}`}>{c}</button>
            ))}
          </div>

          {loading ? <div className="text-center py-12 text-brand-muted">A carregar...</div> : contents.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
              <p className="text-brand-muted">Sem conteúdos disponíveis.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contents.map(c => {
                const Icon = TYPE_ICONS[c.type] || LinkIcon;
                const isVideo = c.type === 'video' && c.url;
                const embedUrl = isVideo ? getEmbedUrl(c.url) : null;
                return (
                  <button key={c.id} onClick={() => isVideo || c.type === 'link' ? setViewContent(c) : openContent(c)} className="w-full text-left bg-white border border-brand-border rounded-2xl p-4 hover:border-brand-green/30 transition-all flex items-start gap-3" data-testid={`content-${c.id}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isVideo && embedUrl ? 'bg-red-50' : 'bg-brand-mint/15'}`}>
                      {isVideo && embedUrl ? <Play size={20} weight="fill" className="text-red-500" /> : <Icon size={20} weight="duotone" className="text-brand-green" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-medium text-sm text-brand-text">{c.title}</h3>
                      {c.description && <Md as="p" className="text-xs text-brand-muted mt-0.5 line-clamp-2">{c.description}</Md>}
                      <div className="flex items-center gap-2 mt-1.5">
                        {c.category && <span className="text-[10px] bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">{c.category}</span>}
                        <span className="text-[10px] text-brand-placeholder uppercase">{c.type}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Drive files section */}
      {activeSection === 'ficheiros' && (
        <>
          {driveFiles.length === 0 ? (
            <div className="text-center py-16">
              <FolderOpen size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
              <p className="text-brand-muted">Sem ficheiros pessoais.</p>
              <p className="text-xs text-brand-placeholder mt-1">A tua nutricionista pode deixar ficheiros aqui para ti.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {driveFiles.map(f => {
                const ext = f.filename?.split('.').pop()?.toLowerCase() || '';
                return (
                  <button key={f.id} onClick={() => downloadFile(f.file_path, f.filename)} className="w-full text-left bg-white border border-brand-border rounded-2xl p-3 hover:border-brand-green/30 transition-all flex items-center gap-3" data-testid={`drive-file-${f.id}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-[10px] font-bold uppercase flex-shrink-0 ${EXT_COLORS[ext] || 'bg-brand-bg text-brand-muted'}`}>{ext}</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm text-brand-text truncate">{f.filename}</h3>
                      <p className="text-[10px] text-brand-muted mt-0.5">{f.created_at?.slice(0, 10)}{f.size ? ` · ${(f.size / 1024).toFixed(0)} KB` : ''}</p>
                    </div>
                    <DownloadSimple size={16} className="text-brand-muted flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Content detail / Video player dialog */}
      <Dialog open={!!viewContent} onOpenChange={() => setViewContent(null)}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[90vh] overflow-y-auto max-w-lg">
          {viewContent && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-brand-text">{viewContent.title}</DialogTitle>
              </DialogHeader>
              {/* Video player */}
              {viewContent.type === 'video' && viewContent.url && (
                <div className="mb-4">
                  <VideoPlayer url={viewContent.url} />
                </div>
              )}
              {/* Link preview */}
              {viewContent.type === 'link' && viewContent.url && !getEmbedUrl(viewContent.url) && (
                <a href={viewContent.url} target="_blank" rel="noreferrer" className="block bg-brand-bg border border-brand-border rounded-xl p-4 mb-4 hover:border-brand-green/30 transition-all">
                  <p className="text-sm text-brand-green font-medium truncate">{viewContent.url}</p>
                  <p className="text-[10px] text-brand-muted mt-1">Abre numa nova janela</p>
                </a>
              )}
              {/* Link that is embeddable video */}
              {viewContent.type === 'link' && getEmbedUrl(viewContent.url) && (
                <div className="mb-4"><VideoPlayer url={viewContent.url} /></div>
              )}
              {/* Description */}
              {viewContent.description && (
                <Md as="p" className="text-sm text-brand-text mb-4">{viewContent.description}</Md>
              )}
              {/* Category */}
              {viewContent.category && (
                <span className="text-[10px] bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">{viewContent.category}</span>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
